import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, HostBinding, Input, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SafeResourceUrl } from "@angular/platform-browser";
import { catchError, first, of, tap, timer } from "rxjs";
import { Animations } from "src/app/animations/animations";
import { ContentPipeModule } from "src/app/services/content/content-pipe.module";
import { DashboardService } from "../dashboard.service";
import { LookerDashboardTypeEnum } from "../models/dashboard.enums";
import { ActivatedRoute } from "@angular/router";
import { LookerDashboardRouteToTitlePipe } from "./looker-dashboard-route-to-title.pipe";

@Component({
    selector: 'looker-dashboard',
    templateUrl: './looker-dashboard.component.html',
    styleUrl: './looker-dashboard.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        ...Animations.fadeInUpAll
    ],
    imports: [
        CommonModule,
        ContentPipeModule,
    ],
    providers: [
        LookerDashboardRouteToTitlePipe
    ]
})
export class LookerDashboardComponent implements OnInit {
  @Input() type: LookerDashboardTypeEnum;

  /** Signed Looker SSO URLs are short-lived; refresh before expiry within a session. */
  private static readonly SSO_REFRESH_MS = 25 * 60 * 1000;

  url = signal<SafeResourceUrl | null>(null);
  loading = signal<boolean>(true);
  error = signal<boolean>(false);
  title: string;

  private resolvedType: LookerDashboardTypeEnum;

  constructor(
    private dashboardService: DashboardService,
    private route: ActivatedRoute,
    private destroyRef: DestroyRef,
    private lookerDashboardRouteToTitlePipe: LookerDashboardRouteToTitlePipe
  ) { }

  @HostBinding('@fadeInUpAllAnimation')
  get hostAnimation() {
    return true;
  }

  @HostBinding('class')
  get hostClass(): string {
    return this.type ? '' : 'page-padding u-display-block no-bottom-padding';
  }

  ngOnInit(): void {
    this.resolvedType = this.type || this.route?.snapshot?.data['type'] as LookerDashboardTypeEnum;
    this.title = this.lookerDashboardRouteToTitlePipe.transform(this.resolvedType);
    this.loadDashboard();
    this.scheduleSsoRefresh();
  }

  onIframeLoad(): void {
    setTimeout(() => this.loading.set(false), 1500);
  }

  retry(): void {
    this.loadDashboard();
  }

  private loadDashboard(): void {
    this.loading.set(true);
    this.error.set(false);
    this.dashboardService.generateDashboardUrl(this.resolvedType).pipe(
      first(),
      tap(url => this.url.set(url)),
      catchError(() => {
        this.error.set(true);
        this.loading.set(false);
        return of(null);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  private scheduleSsoRefresh(): void {
    timer(LookerDashboardComponent.SSO_REFRESH_MS, LookerDashboardComponent.SSO_REFRESH_MS).pipe(
      tap(() => this.loadDashboard()),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }
}
